import type { LateView } from '@/features/notify/api';
import type { Connection, DateInvite, DateSeries, DateView, FriendRequest, GameView, Gift, Invite, LinkRequest, Message, Plan, Pod, ProfileView, Proposal, Session } from '@/api/types';

export interface ServerEvents {
  hello: { user_id: string; server_time: string };
  pong: { server_time: string };
  'match.found': { session: Session };
  'session.updated': { session: Session };
  'game.state': { game: GameView };
  'game.effect': { game_id: string; kind: string; data: unknown };
  'invite.received': { invite: Invite };
  'invite.updated': { invite: Invite };
  'connection.created': { connection: Connection };
  'pod.updated': { pod: Pod };
  'moderation.action': { action: 'warn' | 'suspend' | 'ban'; reason: string; until: string | null };
  // v2 (SPEC_V2 §3, §5, §6)
  'plan.updated': { plan: Plan; plus_until: string | null };
  'friend_request.received': { request: FriendRequest };
  'friend_request.updated': { request: FriendRequest };
  'connection.removed': { connection_id: string };
  'chat.message': { message: Message };
  'chat.deleted': { message: Message };
  'chat.read': { connection_id: string; user_id: string; up_to_id: number };
  'chat.typing': { connection_id: string; user_id: string };
  // v3 (SPEC_V3 §3, §9)
  'game.proposed': { proposal: Proposal };
  'game.proposal_updated': { proposal: Proposal };
  'chat.challenge_updated': { message: Message };
  // v5 (ARCHITECTURE §21): a seat freed on a live Drop you're waitlisted for; retry the join.
  'drop.waitlist': { drop_id: string; seat_available: boolean };
  // v6 (SPEC_V6 §7): per recipient, spice answers redacted.
  'date.state': { date: DateView; session_id?: string };
  /** v6.1: to host and guest; `reminder` once, 15 min before a scheduled date; v9 `direct` when a connection just invited you (K1). */
  'date_invite.updated': { invite: DateInvite; reminder?: boolean; direct?: boolean };
  // v9 area A: personal-link requests (host and requester) and weekly series (both people).
  'link_request.updated': { request: LinkRequest };
  'date_series.updated': { series: DateSeries };
  /** v12.1: someone opened your link (viewer is null while your own private browsing is on). */
  'profile_view.new': { viewer: ProfileView | null };
  /** v9 D: running late / can't make it, to both people (each gets their own view). */
  'date_invite.late': { invite_id: string; late: LateView; by: string };
  // v8 (SPEC_V8 §3, §4): per viewer (the price only for the sender).
  'gift.received': { gift: Gift };
  'gift.removed': { gift: Gift; gift_id: string; by: string };
  'gift.thanked': { gift: Gift; gift_id: string };
  'gift.updated': { gift: Gift };
  /** Photo booth: `shoot` (shot i at at + 3 s + i × gap_ms on both devices) or `cancelled` (void by either person). */
  'gift.moment':
    | { gift_id: string; session_id: string | null; phase: 'shoot'; at: string; shots: number; gap_ms: number; layout: string; theme: string }
    | { gift_id: string; session_id: string | null; phase: 'cancelled'; reason: string; by: string };
  'wallet.updated': { balance: number; locked: boolean };
  // v9 C (SPEC_V9 §C): only a mutual yes is ever sent; consent per viewer; a note to its recipient only.
  'date.second.matched': { session_id: string; second: import('@/features/after/types').SecondView };
  'date.recap.consent': { session_id: string; consent: import('@/features/after/types').ConsentView };
  'date.note.unlocked': { note: import('@/features/after/types').NoteView };
  // v15 Us (SPEC_V15 §6): each person gets their own view; ring and watch go to the space.
  'us.updated': { us: import('@/features/us/types').UsView };
  'us.ended': { space_id: string };
  'us.ring': { space_id: string; from: import('@/api/types').PublicUser; to: 'call' | 'watch'; at: string };
  'us.watch': import('@/features/us/types').WatchView;
  'us.four': import('@/features/us/types').FourView;
  error: { code: string; message: string };
}
export type ServerEventType = keyof ServerEvents;
export type Envelope = { [K in ServerEventType]: { type: K; data: ServerEvents[K] } }[ServerEventType];

export type ConnStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';

type Handler<K extends ServerEventType> = (data: ServerEvents[K]) => void;
type AnyHandler = (env: Envelope) => void;

export interface WsClientOptions {
  getTicket: () => Promise<string>;
  buildUrl?: (ticket: string) => string;
  WebSocketImpl?: typeof WebSocket;
  pingIntervalMs?: number;
  /**
   * No traffic for this long ⇒ the socket is dead (a phone that switched
   * networks keeps a half-open socket that swallows sends). Below the
   * server's 30 s presence cutoff (calls.DisconnectAfter), so we reconnect
   * before the server gives up on us.
   */
  staleAfterMs?: number;
  baseBackoffMs?: number;
  maxBackoffMs?: number;
  /** After this many consecutive failures the status reads `offline` (we keep trying). */
  offlineAfter?: number;
  random?: () => number;
  /** Called when a ticket request fails with an auth error; client stops. */
  isFatal?: (err: unknown) => boolean;
}

export function defaultWsUrl(ticket: string): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/v1/ws?ticket=${encodeURIComponent(ticket)}`;
}

export function backoffDelay(attempt: number, base: number, max: number, random: () => number): number {
  const exp = Math.min(max, base * 2 ** Math.max(0, attempt - 1));
  // "equal jitter": half fixed, half random — avoids thundering herds after outages.
  return Math.round(exp / 2 + (random() * exp) / 2);
}

export class WsClient {
  status: ConnStatus = 'idle';
  attempts = 0;

  private ws: WebSocket | null = null;
  private stopped = true;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastMessageAt = 0;
  private handlers = new Map<string, Set<(d: unknown) => void>>();
  private anyHandlers = new Set<AnyHandler>();
  private statusHandlers = new Set<(s: ConnStatus) => void>();
  private readonly opts: Required<Omit<WsClientOptions, 'isFatal'>> & Pick<WsClientOptions, 'isFatal'>;
  private generation = 0;

  constructor(opts: WsClientOptions) {
    this.opts = {
      buildUrl: defaultWsUrl,
      WebSocketImpl: typeof WebSocket !== 'undefined' ? WebSocket : (undefined as unknown as typeof WebSocket),
      pingIntervalMs: 10_000,
      staleAfterMs: 18_000, // caught on the 2nd silent ping (20 s), well inside 30 s
      baseBackoffMs: 500,
      maxBackoffMs: 15_000,
      offlineAfter: 4,
      random: Math.random,
      ...opts,
    };
  }

  // ---------- public API

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempts = 0;
    this.setStatus('connecting');
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.generation++;
    this.clearTimers();
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
      try {
        ws.close(1000, 'bye');
      } catch {
        /* ignore */
      }
    }
    this.setStatus('idle');
  }

  /** Reconnect immediately (e.g. browser came back online). */
  kick(): void {
    if (this.stopped || this.status === 'connected') return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    // A socket still connecting would otherwise be overwritten and left
    // open: a second live connection the server counts until it times out.
    const old = this.ws;
    if (old) {
      this.ws = null;
      this.generation++;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      old.onopen = old.onclose = old.onerror = old.onmessage = null;
      try {
        old.close(4000, 'kick');
      } catch {
        /* ignore */
      }
    }
    void this.connect();
  }

  /**
   * The page is back in front (or the network is back): a socket that's
   * been quiet past a ping interval may have died while timers were
   * frozen, so recycle it now; a live one just gets a ping.
   */
  wake(): void {
    if (this.stopped) return;
    if (this.status !== 'connected') return this.kick();
    if (Date.now() - this.lastMessageAt > this.opts.pingIntervalMs + 2_000) return this.recycle();
    this.send('ping', {});
  }

  send(type: string, data: unknown): boolean {
    const OPEN = this.opts.WebSocketImpl?.OPEN ?? 1;
    if (!this.ws || this.ws.readyState !== OPEN) return false;
    try {
      this.ws.send(JSON.stringify({ type, data }));
      return true;
    } catch {
      return false;
    }
  }

  get connected(): boolean {
    return this.status === 'connected';
  }

  on<K extends ServerEventType>(type: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as (d: unknown) => void);
    return () => set!.delete(handler as (d: unknown) => void);
  }

  onAny(handler: AnyHandler): () => void {
    this.anyHandlers.add(handler);
    return () => this.anyHandlers.delete(handler);
  }

  onStatus(handler: (s: ConnStatus) => void): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  /** Dispatch an envelope as if it came from the server (tests, local echo). */
  dispatch(env: Envelope): void {
    const set = this.handlers.get(env.type);
    if (set) for (const h of [...set]) safeCall(() => h(env.data));
    for (const h of [...this.anyHandlers]) safeCall(() => h(env));
  }

  // ---------- internals

  private setStatus(s: ConnStatus): void {
    if (this.status === s) return;
    this.status = s;
    for (const h of [...this.statusHandlers]) safeCall(() => h(s));
  }

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.pingTimer = null;
    this.retryTimer = null;
  }

  private async connect(): Promise<void> {
    const gen = ++this.generation;
    let ticket: string;
    try {
      ticket = await this.opts.getTicket(); // always a fresh, single-use ticket
    } catch (e) {
      if (gen !== this.generation || this.stopped) return;
      if (this.opts.isFatal?.(e)) {
        this.stop();
        return;
      }
      this.scheduleReconnect();
      return;
    }
    if (gen !== this.generation || this.stopped) return;

    let ws: WebSocket;
    try {
      ws = new this.opts.WebSocketImpl(this.opts.buildUrl(ticket));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (gen !== this.generation) return;
      this.attempts = 0;
      this.lastMessageAt = Date.now();
      this.setStatus('connected');
      this.send('ping', {});
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => this.heartbeat(), this.opts.pingIntervalMs);
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (gen !== this.generation) return;
      this.lastMessageAt = Date.now();
      let env: Envelope | null = null;
      try {
        env = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as Envelope;
      } catch {
        return;
      }
      if (!env || typeof env.type !== 'string') return;
      this.dispatch(env);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
    ws.onclose = () => {
      if (gen !== this.generation) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (this.stopped) return;
      this.scheduleReconnect();
    };
  }

  private heartbeat(): void {
    // Every ping gets a pong, so silence means a dead socket: recycle it.
    if (Date.now() - this.lastMessageAt > this.opts.staleAfterMs) return this.recycle();
    this.send('ping', {});
  }

  /** Drop the current socket and reconnect at once (a half-open socket may never fire onclose). */
  private recycle(): void {
    const ws = this.ws;
    if (!ws) return this.kick();
    this.ws = null;
    this.generation++;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
    try {
      ws.close(4000, 'stale');
    } catch {
      /* ignore */
    }
    this.setStatus('reconnecting');
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    void this.connect();
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.attempts++;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    this.setStatus(offline || this.attempts > this.opts.offlineAfter ? 'offline' : 'reconnecting');
    const delay = backoffDelay(this.attempts, this.opts.baseBackoffMs, this.opts.maxBackoffMs, this.opts.random);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }
}

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    console.error('[ws] handler error', e);
  }
}
